import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faXmark } from '@fortawesome/free-solid-svg-icons';
import { v2Colors, v2FontFamily } from '../../formInputsV2/styles/tokens';
import {
  headerV2Css,
  headerIconV2Css,
  headerTitleV2Css,
} from '../../../screens/CompletedQuotes/CompletedV2.styles';

const ModalStyles = styled.div`
  .modal-overlay {
    display: ${(props) => (props.isOpen ? 'flex' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 16px;
    background: rgba(16, 24, 40, 0.55);
    backdrop-filter: blur(3px);
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal-content {
    overflow: hidden;
    width: 100%;
    max-width: 480px;
    background: ${v2Colors.inputBackground};
    border-radius: 16px;
    box-shadow: 0 24px 60px rgba(16, 24, 40, 0.28);
    font-family: ${v2FontFamily};
    color: ${v2Colors.text};
  }

  .modal-header {
    ${headerV2Css}
    gap: 13px;

    .modal-header-icon {
      ${headerIconV2Css}
    }

    .modal-title {
      ${headerTitleV2Css}
      flex: 1;
    }

    .modal-close {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 32px;
      height: 32px;
      padding: 0;
      border: 1px solid rgba(255, 255, 255, 0.4);
      border-radius: 10px;
      background: rgba(255, 255, 255, 0.15);
      font-size: 14px;
      color: #ffffff;
      cursor: pointer;
      transition: background-color 150ms ease;

      &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.3);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }

  .modal-body {
    padding: 22px 22px 6px;
  }

  .form-group {
    margin-bottom: 0;
  }

  .form-label {
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 600;
    color: ${v2Colors.text};
  }

  /* the email field keeps its bootstrap validation classes (is-valid / is-invalid) */
  .form-control {
    height: 46px;
    padding: 0 14px;
    border: 1px solid ${v2Colors.border};
    border-radius: 10px;
    background: ${v2Colors.inputBackground};
    font-family: ${v2FontFamily};
    font-size: 15px;
    color: ${v2Colors.text};
    transition: border-color 150ms ease, box-shadow 150ms ease;

    &::placeholder {
      color: ${v2Colors.textFaint};
    }

    &:focus {
      border-color: ${v2Colors.primary};
      box-shadow: 0 0 0 3px ${v2Colors.primarySoftHover};
      outline: none;
    }

    &.is-valid {
      border-color: ${v2Colors.primary};
      background-image: none;
    }

    &.is-invalid {
      border-color: ${v2Colors.danger};
      background-image: none;

      &:focus {
        box-shadow: 0 0 0 3px ${v2Colors.dangerSoftHover};
      }
    }
  }

  /* plain block instead of bootstrap's wrapping flex row, so no extra space opens up before the message */
  .input-group {
    display: block;
  }

  /* bootstrap sets width: 1% on inputs inside an input-group (it relies on flex); block layout needs the full width */
  .input-group > .form-control {
    display: block;
    width: 100%;
    margin: 0;
  }

  .invalid-feedback {
    display: block;
    width: 100%;
    margin: 4px 0 0;
    font-size: 12.5px;
    line-height: 1.3;
    color: ${v2Colors.danger};
  }

  .modal-actions {
    display: flex;
    gap: 12px;
    padding: 22px;
  }

  .btn-cancel,
  .btn-submit {
    flex: 1;
    height: 46px;
    padding: 0 16px;
    border-radius: 12px;
    font-family: ${v2FontFamily};
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    transition: background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .btn-cancel {
    border: 1px solid ${v2Colors.border};
    background: ${v2Colors.inputBackground};
    color: ${v2Colors.text};

    &:hover:not(:disabled) {
      background: ${v2Colors.surface};
    }
  }

  .btn-submit {
    border: 1px solid ${v2Colors.primary};
    background: ${v2Colors.primary};
    color: #ffffff;

    &:hover:not(:disabled) {
      background: ${v2Colors.primaryHover};
      border-color: ${v2Colors.primaryHover};
      box-shadow: 0 6px 14px rgba(0, 166, 81, 0.28);
    }
  }

  @media (max-width: 480px) {
    .modal-body {
      padding: 18px 16px 4px;
    }

    .modal-actions {
      flex-direction: column-reverse;
      gap: 10px;
      padding: 18px 16px 16px;
    }
  }
`;

function DocumentsToSendModal({
  isOpen,
  onClose,
  register,
  errors,
  formData,
  sendDocumentsToClient,
  sendingDocuments,
  trigger,
}) {
  // validate first, so an empty / wrong email shows its error on the input instead of being sent
  const handleSubmit = async () => {
    if (trigger) {
      const isValid = await trigger('email');
      if (!isValid) return;
    }
    sendDocumentsToClient(formData);
  };

  return (
    <ModalStyles isOpen={isOpen}>
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <div className="modal-header-icon">
              <FontAwesomeIcon icon={faPaperPlane} />
            </div>
            <h2 className="modal-title">Invia Documenti al Cliente</h2>
            <button
              type="button"
              className="modal-close"
              aria-label="Chiudi"
              onClick={onClose}
              disabled={sendingDocuments}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Indirizzo Email</label>
              <div className="input-group has-validation">
                <input
                  type="email"
                  className={`form-control py-2 ${errors.email ? 'is-invalid' : ''} ${!errors.email && formData.email ? 'is-valid' : ''}`}
                  {...register('email')}
                  placeholder="Inserisci indirizzo email"
                />
                {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
              </div>
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn-cancel" onClick={onClose} disabled={sendingDocuments}>
              Annulla
            </button>
            <button className="btn-submit" onClick={handleSubmit} disabled={sendingDocuments}>
              {sendingDocuments ? 'Invio in corso...' : 'Invia'}
            </button>
          </div>
        </div>
      </div>
    </ModalStyles>
  );
}

export default DocumentsToSendModal;
